/*
 * Matching a Voonix campaign key to a site brand link, for the mapping screens.
 *
 * Normally the key is our own: we put it in the link, Voonix reports it back, and the
 * link's `internalNote` equals it. NetRefer brands are different — the key Voonix
 * reports is NetRefer's numeric promotion id, which sits in the link as `pid=`, while
 * the internalNote stays a mnemonic (`cmp1`, `nmbn`, …). For those, the note never
 * matches and the `pid` is the only thing tying the campaign to the link.
 *
 * So the candidate panes highlight both. This is a hint for the operator who then
 * clicks Map, which is why it is not scoped to NetRefer here — the server's automap
 * rule (`helpers/automap-pid.js`) is the scoped one, since it writes without a human.
 */

/** The `pid` carried by a tracking link, or null. URL-parsed, so `xpid=` doesn't count. */
export function linkCampaignKey(affiliateLink) {
    if (!affiliateLink) return null;
    try {
        const value = new URL(affiliateLink).searchParams.get('pid');
        if (value === null) return null;
        const trimmed = String(value).trim();
        return trimmed === '' ? null : trimmed;
    } catch (e) {
        return null;   // not a parseable URL — simply no key
    }
}

/** Exact comparison, trimmed; an empty value on either side never matches. */
function keysEqual(a, b) {
    if (a === null || a === undefined || b === null || b === undefined) return false;
    const left = String(a).trim();
    const right = String(b).trim();
    if (left === '' || right === '') return false;
    return left === right;
}

/**
 * How a link relates to a campaign key: 'internalNote', 'linkPid', or null.
 * The note takes precedence, mirroring the order the server tries its rules in.
 */
export function campaignKeyMatch(siteLink, campaignKey) {
    if (!siteLink || campaignKey === null || campaignKey === undefined) return null;
    if (keysEqual(siteLink.internalNote, campaignKey)) return 'internalNote';
    if (keysEqual(linkCampaignKey(siteLink.affiliateLink), campaignKey)) return 'linkPid';
    return null;
}

/** True when the link is a candidate for that campaign key by either rule. */
export function matchesCampaignKey(siteLink, campaignKey) {
    return campaignKeyMatch(siteLink, campaignKey) !== null;
}
